import { PdfBrandConfig } from '@common/types';
import { createContext, ReactNode, useContext, useMemo } from 'react';
import { BrandingMode } from 'enums';
import { PdfBrandConfigs, usePdfBrandConfigs } from 'broker/hooks/usePdfBrandConfig';
import useSubmissionsWorkspace from 'broker/pages/SubmissionWorkspacePage/store/useSubmissionWorkspace';

interface BrandConfigContextState {
  brandConfigs: PdfBrandConfigs | undefined;
  isClientBranded: boolean;
  isLoading: boolean;
  getBrandConfig: (brandingMode?: BrandingMode) => PdfBrandConfig | undefined;
}

const BrandConfigStateContext = createContext<BrandConfigContextState | undefined>(undefined);

interface BrandConfigProviderProps {
  children: ReactNode;
}

export function BrandConfigProvider({ children }: BrandConfigProviderProps) {
  const { partialSubmission } = useSubmissionsWorkspace();

  const { brandConfigs, isClientBranded, isLoading } = usePdfBrandConfigs({
    organizationId: partialSubmission?.organizationId || '',
  });

  const getBrandConfig = useMemo(
    () =>
      (brandingMode: BrandingMode = BrandingMode.Organization) =>
        brandConfigs?.[brandingMode],
    [brandConfigs],
  );

  const state: BrandConfigContextState = useMemo(
    () => ({
      brandConfigs,
      isClientBranded,
      isLoading,
      getBrandConfig,
    }),
    [brandConfigs, isClientBranded, isLoading, getBrandConfig],
  );

  return <BrandConfigStateContext.Provider value={state}>{children}</BrandConfigStateContext.Provider>;
}

export function useBrandConfig(): BrandConfigContextState {
  const context = useContext(BrandConfigStateContext);
  if (context === undefined) {
    throw new Error('useBrandConfig must be used within a BrandConfigProvider');
  }
  return context;
}

export function useBrandConfigByMode(brandingMode: BrandingMode) {
  const { getBrandConfig, isLoading } = useBrandConfig();
  return {
    brandConfig: getBrandConfig(brandingMode),
    isLoading,
  };
}
